import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { Loader2, DollarSign, AlertTriangle, CheckCircle, Info, X } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';

const REFUND_REASONS = [
  { value: 'customer_request', label: 'Customer Request' },
  { value: 'service_issue', label: 'Service Issue' },
  { value: 'duplicate_booking', label: 'Duplicate Booking' },
  { value: 'weather', label: 'Weather/Conditions' },
  { value: 'operator_cancellation', label: 'Operator Cancellation' },
  { value: 'other', label: 'Other' }
];

const RefundModal = ({ isOpen, onClose, bookingId, onRefundComplete }) => {
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [refundInfo, setRefundInfo] = useState(null);
  const [error, setError] = useState(null);
  
  // Form state
  const [refundType, setRefundType] = useState('full');
  const [partialAmount, setPartialAmount] = useState('');
  const [reason, setReason] = useState('customer_request');
  const [cancelBooking, setCancelBooking] = useState(false);
  const [notifyCustomer, setNotifyCustomer] = useState(true);

  useEffect(() => {
    if (isOpen && bookingId) {
      fetchRefundInfo();
    }
  }, [isOpen, bookingId]);

  const fetchRefundInfo = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get(`/admin/bookings/${bookingId}/refund-info`);
      setRefundInfo(response.data);
      
      // Pre-fill partial amount with max refundable
      if (response.data?.refundHistory?.maxRefundable > 0) {
        setPartialAmount(response.data.refundHistory.maxRefundable.toFixed(2));
      }
      
      // Set default cancel booking based on API response (ON for future trips)
      if (response.data?.defaults?.cancelBooking !== undefined) {
        setCancelBooking(response.data.defaults.cancelBooking);
      }
    } catch (err) {
      console.error('Error fetching refund info:', err);
      setError(err.response?.data?.detail || 'Failed to load refund information');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!refundInfo) return;

    const maxRefundable = refundInfo.refundHistory.maxRefundable;
    const refundAmount = refundType === 'full' ? maxRefundable : parseFloat(partialAmount);

    // Validation
    if (refundType === 'partial') {
      if (!partialAmount || isNaN(refundAmount) || refundAmount <= 0) {
        toast.error('Please enter a valid refund amount');
        return;
      }
      if (refundAmount > maxRefundable) {
        toast.error(`Amount exceeds maximum refundable: $${maxRefundable.toFixed(2)}`);
        return;
      }
    }

    if (maxRefundable <= 0) {
      toast.error('No refundable amount remaining');
      return;
    }

    try {
      setProcessing(true);
      
      const response = await api.post(`/admin/bookings/${bookingId}/refund`, {
        refundType,
        amount: refundType === 'partial' ? refundAmount : null,
        reason,
        cancelBooking,
        notifyCustomer
      });

      toast.success(response.data.message || 'Refund processed successfully');
      
      if (onRefundComplete) {
        onRefundComplete(response.data);
      }
      
      onClose();
    } catch (err) {
      console.error('Refund error:', err);
      toast.error(err.response?.data?.detail || 'Failed to process refund');
    } finally {
      setProcessing(false);
    }
  };

  const handleMarkAsRefunded = async () => {
    try {
      setProcessing(true);
      
      const response = await api.post(`/admin/bookings/${bookingId}/mark-refunded`, {
        reason: 'manual_refund',
        amount: refundInfo?.breakdown?.subtotalRefundable || 0,
        notifyCustomer: false
      });

      toast.success('Booking marked as refunded');
      
      if (onRefundComplete) {
        onRefundComplete(response.data);
      }
      
      onClose();
    } catch (err) {
      console.error('Mark refunded error:', err);
      toast.error(err.response?.data?.detail || 'Failed to mark as refunded');
    } finally {
      setProcessing(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="fixed inset-0 z-[10001] flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-white rounded-lg shadow-2xl w-full max-w-[500px] mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-blue-600" />
            Process Refund
          </h2>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
              <span className="ml-2 text-gray-600">Loading refund information...</span>
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-red-700">{error}</p>
            </div>
          ) : !refundInfo?.hasStripePayment ? (
            <div className="space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
                <Info className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                <div className="text-amber-800">
                  <p className="font-medium mb-1">Manual Payment Detected</p>
                  <p className="text-sm">
                    This booking was marked as &quot;Already Paid&quot; outside of Stripe (cash, bank transfer, etc.). 
                    You will need to process the refund manually through your original payment method.
                  </p>
                </div>
              </div>
              
              {/* Booking Info */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-gray-500">Customer:</span>
                    <p className="font-medium">{refundInfo?.customerName}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Service:</span>
                    <p className="font-medium">{refundInfo?.serviceName}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Amount:</span>
                    <p className="font-medium">{formatCurrency(refundInfo?.breakdown?.subtotalRefundable)}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Confirmation:</span>
                    <p className="font-medium">{refundInfo?.confirmationCode}</p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-100 rounded-lg p-4 text-center">
                <p className="text-sm text-gray-600 mb-3">
                  Would you like to mark this booking as refunded for your records?
                </p>
                <Button 
                  onClick={handleMarkAsRefunded}
                  disabled={processing}
                  className="bg-orange-500 hover:bg-orange-600"
                >
                  {processing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    'Mark as Refunded'
                  )}
                </Button>
              </div>
            </div>
          ) : refundInfo?.refundHistory?.maxRefundable <= 0 ? (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
              <p className="text-green-700">This booking has already been fully refunded.</p>
            </div>
          ) : (
            <>
              {/* Booking Info */}
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-gray-500">Customer:</span>
                    <p className="font-medium">{refundInfo.customerName}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Service:</span>
                    <p className="font-medium">{refundInfo.serviceName}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Confirmation:</span>
                    <p className="font-medium">{refundInfo.confirmationCode}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Payment Status:</span>
                    <p className="font-medium capitalize">{refundInfo.paymentStatus}</p>
                  </div>
                </div>
              </div>

              {/* Price Breakdown */}
              <div className="border rounded-lg p-4 mb-4">
                <h4 className="font-medium text-gray-900 mb-3">Amount Breakdown</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Service Price</span>
                    <span>{formatCurrency(refundInfo.breakdown.servicePrice)}</span>
                  </div>
                  {refundInfo.breakdown.addOnsTotal > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Add-ons</span>
                      <span>{formatCurrency(refundInfo.breakdown.addOnsTotal)}</span>
                    </div>
                  )}
                  {refundInfo.breakdown.salesTax > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Sales Tax</span>
                      <span>{formatCurrency(refundInfo.breakdown.salesTax)}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-medium border-t pt-2">
                    <span className="text-green-600">Refundable Amount</span>
                    <span className="text-green-600">{formatCurrency(refundInfo.breakdown.subtotalRefundable)}</span>
                  </div>
                  
                  <div className="flex justify-between text-gray-400 text-xs pt-2">
                    <span>Platform Fee (non-refundable)</span>
                    <span>{formatCurrency(refundInfo.breakdown.platformFee)}</span>
                  </div>
                  <div className="flex justify-between text-gray-400 text-xs">
                    <span>Processing Fee (non-refundable)</span>
                    <span>{formatCurrency(refundInfo.breakdown.processingFee)}</span>
                  </div>
                  
                  {refundInfo.refundHistory.alreadyRefunded > 0 && (
                    <div className="flex justify-between text-orange-600 border-t pt-2">
                      <span>Already Refunded</span>
                      <span>-{formatCurrency(refundInfo.refundHistory.alreadyRefunded)}</span>
                    </div>
                  )}
                  
                  <div className="flex justify-between font-bold text-lg border-t pt-2 mt-2">
                    <span>Max Refundable Now</span>
                    <span className="text-blue-600">{formatCurrency(refundInfo.refundHistory.maxRefundable)}</span>
                  </div>
                </div>
              </div>

              {/* Refund Form */}
              <div className="space-y-4">
                {/* Refund Type */}
                <div className="space-y-2">
                  <Label>Refund Type</Label>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="refundType"
                        value="full"
                        checked={refundType === 'full'}
                        onChange={(e) => setRefundType(e.target.value)}
                        className="w-4 h-4 text-blue-600"
                      />
                      <span>Full Refund ({formatCurrency(refundInfo.refundHistory.maxRefundable)})</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="refundType"
                        value="partial"
                        checked={refundType === 'partial'}
                        onChange={(e) => setRefundType(e.target.value)}
                        className="w-4 h-4 text-blue-600"
                      />
                      <span>Partial Refund</span>
                    </label>
                  </div>
                </div>

                {/* Partial Amount */}
                {refundType === 'partial' && (
                  <div className="space-y-2">
                    <Label htmlFor="partialAmount">Refund Amount</Label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                      <Input
                        id="partialAmount"
                        type="number"
                        step="0.01"
                        min="0.01"
                        max={refundInfo.refundHistory.maxRefundable}
                        value={partialAmount}
                        onChange={(e) => {
                          const val = parseFloat(e.target.value);
                          const max = refundInfo.refundHistory.maxRefundable;
                          // Enforce max limit in real-time
                          if (!isNaN(val) && val > max) {
                            setPartialAmount(max.toFixed(2));
                          } else {
                            setPartialAmount(e.target.value);
                          }
                        }}
                        onBlur={(e) => {
                          // On blur, clamp to valid range
                          const val = parseFloat(e.target.value);
                          const max = refundInfo.refundHistory.maxRefundable;
                          if (!isNaN(val)) {
                            if (val > max) setPartialAmount(max.toFixed(2));
                            else if (val < 0.01) setPartialAmount('0.01');
                            else setPartialAmount(val.toFixed(2));
                          }
                        }}
                        className={`pl-7 ${parseFloat(partialAmount) > refundInfo.refundHistory.maxRefundable ? 'border-red-500 focus:ring-red-500' : ''}`}
                        placeholder="0.00"
                      />
                    </div>
                    {parseFloat(partialAmount) > refundInfo.refundHistory.maxRefundable && (
                      <p className="text-xs text-red-600 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" />
                        Amount cannot exceed {formatCurrency(refundInfo.refundHistory.maxRefundable)}
                      </p>
                    )}
                    {!(parseFloat(partialAmount) > refundInfo.refundHistory.maxRefundable) && (
                      <p className="text-xs text-gray-500">
                        Maximum refundable: {formatCurrency(refundInfo.refundHistory.maxRefundable)}
                      </p>
                    )}
                  </div>
                )}

                {/* Reason */}
                <div className="space-y-2">
                  <Label>Reason for Refund</Label>
                  <Select value={reason} onValueChange={setReason}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select reason" />
                    </SelectTrigger>
                    <SelectContent className="z-[10002]">
                      {REFUND_REASONS.map((r) => (
                        <SelectItem key={r.value} value={r.value}>
                          {r.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Options */}
                <div className="space-y-3 pt-2">
                  {/* Cancel booking option - only for future trips */}
                  {refundInfo?.tripTiming?.canCancel ? (
                    <label className="flex items-center gap-3 cursor-pointer">
                      <Checkbox
                        checked={cancelBooking}
                        onCheckedChange={setCancelBooking}
                      />
                      <span className="text-sm">Also cancel this booking</span>
                    </label>
                  ) : (
                    <div className="flex items-center gap-2 text-sm text-gray-500 bg-gray-50 p-2 rounded">
                      <Info className="h-4 w-4" />
                      <span>{refundInfo?.tripTiming?.cancelNote || 'Past trips cannot be cancelled'}</span>
                    </div>
                  )}
                  
                  <label className="flex items-center gap-3 cursor-pointer">
                    <Checkbox
                      checked={notifyCustomer}
                      onCheckedChange={setNotifyCustomer}
                    />
                    <span className="text-sm">Send email notification to customer</span>
                  </label>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 p-4 border-t bg-gray-50">
          <Button variant="outline" onClick={onClose} disabled={processing}>
            Cancel
          </Button>
          {refundInfo?.hasStripePayment && refundInfo?.refundHistory?.maxRefundable > 0 && (
            <Button 
              onClick={handleSubmit} 
              disabled={processing || loading}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {processing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <DollarSign className="w-4 h-4 mr-2" />
                  Process Refund
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

export default RefundModal;
